import { RobotCalendarEvent } from '../components/RobotSchedule/RobotSchedule.types';

// Overlap groups larger than this are collapsed into a peeking stack instead of
// splitting into ever-narrower side-by-side columns.
const STACK_THRESHOLD = 2;
// Width (and floor width) of every event once a stack forms.
const STACK_EVENT_WIDTH_PX = 48;
// Horizontal cascade between consecutive stacked events.
const STACK_STEP_PX = 24;
// Vertical cascade between consecutive stacked events, purely cosmetic (fan effect).
const STACK_NUDGE_PX = 6;

interface SlotRange {
  top: number;
  height: number;
}

interface SlotMetricsLike {
  getRange: (start: Date, end: Date) => SlotRange;
}

interface AccessorsLike<TEvent> {
  start: (event: TEvent) => Date;
  end: (event: TEvent) => Date;
}

interface DayLayoutStyle {
  top: number | string;
  height: number | string;
  width: number | string;
  xOffset: number | string;
}

interface StyledEvent<TEvent> {
  event: TEvent;
  style: DayLayoutStyle;
}

interface DayLayoutArgs<TEvent> {
  events: TEvent[];
  minimumStartDifference: number;
  slotMetrics: SlotMetricsLike;
  accessors: AccessorsLike<TEvent>;
}

interface EventRange<TEvent> {
  event: TEvent;
  top: number;
  height: number;
}

const rangesOverlap = <TEvent>(a: EventRange<TEvent>, b: EventRange<TEvent>): boolean => {
  const aEnd = a.top + a.height;
  const bEnd = b.top + b.height;

  return a.top < bEnd && b.top < aEnd;
};

/** Connected components of mutually-overlapping event ranges, i.e. "events sharing a time slot". */
const groupOverlappingRanges = <TEvent>(ranges: EventRange<TEvent>[]): EventRange<TEvent>[][] => {
  const groups: EventRange<TEvent>[][] = [];
  const visited = new Set<number>();

  ranges.forEach((range, index) => {
    if (visited.has(index)) return;

    const group = [range];
    visited.add(index);

    let cursor = 0;

    while (cursor < group.length) {
      const member = group[cursor];

      ranges.forEach((other, otherIndex) => {
        if (!visited.has(otherIndex) && rangesOverlap(member, other)) {
          group.push(other);
          visited.add(otherIndex);
        }
      });

      cursor += 1;
    }

    groups.push(group);
  });

  return groups;
};

/** Faithful to react-big-calendar's built-in "no-overlap" column split, for groups of 1-2 events. */
const layoutSideBySide = (count: number, index: number): Pick<DayLayoutStyle, 'width' | 'xOffset'> => {
  const size = 100 / count;
  const left = index * size;
  const padding = index === 0 ? 0 : 3;

  return {
    width: `calc(${size}% - ${padding}px)`,
    xOffset: `calc(${left}% + ${padding}px)`,
  };
};

/** Peeking card stack for groups of >2 events: full-width primary, fixed-width cascading peeks behind it. */
const layoutStack = (count: number, index: number): Pick<DayLayoutStyle, 'width' | 'xOffset'> => {
  if (index === 0) {
    return {
      width: `max(${STACK_EVENT_WIDTH_PX}px, calc(100% - ${(count - 1) * STACK_STEP_PX}px))`,
      xOffset: 0,
    };
  }

  // Distance of this card from the last (rightmost) one; 0 pins it flush with the column's right edge.
  const distanceFromLast = count - 1 - index;
  const fromRight = STACK_EVENT_WIDTH_PX + distanceFromLast * STACK_STEP_PX;

  return {
    width: `${STACK_EVENT_WIDTH_PX}px`,
    xOffset: `calc(100% - ${fromRight}px)`,
  };
};

export const robotScheduleDayLayoutAlgorithm = ({
  events,
  slotMetrics,
  accessors,
}: DayLayoutArgs<RobotCalendarEvent>): StyledEvent<RobotCalendarEvent>[] => {
  const ranges: EventRange<RobotCalendarEvent>[] = events.map(event => {
    const { top, height } = slotMetrics.getRange(accessors.start(event), accessors.end(event));

    return { event, top, height };
  });

  const groups = groupOverlappingRanges(ranges);
  // Stacked events are reordered so later (narrower) peeks are painted after — and therefore
  // on top of — earlier ones; everything else keeps its original relative order.
  const stackPaintOrder = new Map<RobotCalendarEvent, number>();

  const styledEvents = groups.flatMap(group => {
    const sorted = [...group].sort((a, b) => accessors.start(a.event).getTime() - accessors.start(b.event).getTime());
    const isStack = sorted.length > STACK_THRESHOLD;

    return sorted.map((range, index) => {
      if (isStack) stackPaintOrder.set(range.event, range.top * 1000 + index);

      const { width, xOffset } = isStack ? layoutStack(sorted.length, index) : layoutSideBySide(sorted.length, index);
      const nudgePx = isStack && index > 0 ? index * STACK_NUDGE_PX : 0;
      const top = nudgePx > 0 ? `calc(${range.top}% + ${nudgePx}px)` : range.top;
      const height = nudgePx > 0 ? `calc(${range.height}% - 2px - ${nudgePx}px)` : `calc(${range.height}% - 2px)`;

      return {
        event: range.event,
        style: {
          top,
          height,
          width,
          xOffset,
        },
      };
    });
  });

  return styledEvents.sort((a, b) => (stackPaintOrder.get(a.event) ?? 0) - (stackPaintOrder.get(b.event) ?? 0));
};
